'use client'

import { CheckIcon } from '@repo/ui/CheckIcon'
import { CrossIcon } from '@repo/ui/CrossIcon'
import { PasswordHiddenIcon } from '@repo/ui/PasswordHiddenIcon'
import { PasswordShowingIcon } from '@repo/ui/PasswordShowingIcon'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import MUIIconButton from '@mui/material/IconButton'
import MUIInput from '@mui/material/TextField'
import MUIInputAdornment from '@mui/material/InputAdornment'
import MUILinearProgress from '@mui/material/LinearProgress'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import {
  ospreyPasswordHasSufficientLength,
  ospreyPasswordHasUppercase,
  ospreyPasswordHasLowercase,
  ospreyPasswordHasNumber,
  ospreyPasswordHasSpecialCharacter,
  setMinimumPasswordLength
} from '@repo/validation/schema_ospreyAPI'

export const PasswordInput = ({
  onChange,
  label = 'Password',
  autoFocus = false,
  autoComplete = 'current-password',
  showPasswordStrengthIndicator = false,
  onValidationChange,
  API_DI
}) => {
  const [password, setPassword] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const { getMinimumPasswordLength, minimumPasswordLength } = useAuthenticationStore()
  const theme = useTheme()

  // password validation
  const [passwordHasLength, setPasswordHasLength] = useState(0)
  const [passwordHasUppercase, setPasswordHasUppercase] = useState(0)
  const [passwordHasLowercase, setPasswordHasLowercase] = useState(0)
  const [passwordHasNumber, setPasswordHasNumber] = useState(0)
  const [passwordHasSpecialCharacter, setPasswordHasSpecialCharacter] = useState(0)
  const [passwordScore, setPasswordScore] = useState(0)

  const preventDefault = (event) => {
    event.preventDefault()
  }

  const handleChange = (e) => {
    onChange(e)
    setPassword(e.target.value)
  }

  useEffect(() => {
    if (!showPasswordStrengthIndicator) return
    API_DI ? API_DI() : getMinimumPasswordLength()
  }, [getMinimumPasswordLength, API_DI, showPasswordStrengthIndicator])

  useEffect(() => {
    setMinimumPasswordLength(minimumPasswordLength)
  }, [minimumPasswordLength])

  useEffect(() => {
    if (!showPasswordStrengthIndicator) return

    const validatePassword = async () => {
      try {
        await ospreyPasswordHasSufficientLength.validate({ password })
        setPasswordHasLength(1)
      } catch (e) {
        setPasswordHasLength(0)
      }

      try {
        await ospreyPasswordHasUppercase.validate({ password })
        setPasswordHasUppercase(1)
      } catch (e) {
        setPasswordHasUppercase(0)
      }

      try {
        await ospreyPasswordHasLowercase.validate({ password })
        setPasswordHasLowercase(1)
      } catch (e) {
        setPasswordHasLowercase(0)
      }

      try {
        await ospreyPasswordHasNumber.validate({ password })
        setPasswordHasNumber(1)
      } catch (e) {
        setPasswordHasNumber(0)
      }

      try {
        await ospreyPasswordHasSpecialCharacter.validate({ password })
        setPasswordHasSpecialCharacter(1)
      } catch (e) {
        setPasswordHasSpecialCharacter(0)
      }

      const score =
        passwordHasLength +
        passwordHasUppercase +
        passwordHasLowercase +
        passwordHasNumber +
        passwordHasSpecialCharacter
      setPasswordScore(score)

      // tell the parent component if the password is valid or not
      onValidationChange(score === 5)
    }

    validatePassword()
  }, [
    password,
    showPasswordStrengthIndicator,
    passwordHasLength,
    passwordHasUppercase,
    passwordHasLowercase,
    passwordHasNumber,
    passwordHasSpecialCharacter,
    onValidationChange
  ])

  const validationList = [
    { label: 'At least one lowercase character', valid: passwordHasLowercase },
    { label: 'At least one uppercase character', valid: passwordHasUppercase },
    { label: 'At least one numeral', valid: passwordHasNumber },
    { label: 'At least one special character', valid: passwordHasSpecialCharacter },
    { label: `At least ${minimumPasswordLength} characters`, valid: passwordHasLength }
  ]

  return (
    <>
      <MUIInput
        data-testid="PasswordInput"
        id="password"
        name="password"
        autoComplete={autoComplete}
        label={label}
        type={passwordVisible ? 'text' : 'password'}
        sx={{ width: '100%' }}
        onChange={handleChange}
        required
        autoFocus={autoFocus}
        InputProps={{
          endAdornment: (
            <MUIInputAdornment position="end">
              <MUIIconButton
                aria-label="toggle password visibility"
                onClick={() => setPasswordVisible(!passwordVisible)}
                onMouseDown={preventDefault}
                onMouseUp={preventDefault}
                edge="end"
              >
                {passwordVisible ? <PasswordShowingIcon /> : <PasswordHiddenIcon />}
              </MUIIconButton>
            </MUIInputAdornment>
          )
        }}
      />

      {showPasswordStrengthIndicator ? (
        <>
          <MUILinearProgress
            variant="determinate"
            value={(100 * passwordScore) / 5}
            color={getIndicatorColor(passwordScore)}
            sx={{
              width: '100%',
              height: '10px',
              borderRadius: '10px',
              backgroundColor: 'background.slate100',
              mb: 2
            }}
          />
          {renderValidationList(validationList, theme)}
        </>
      ) : null}
    </>
  )
}

const getIndicatorColor = (score) => {
  switch (score) {
    case 1:
      return 'error'
    case 2:
      return 'errorLight'
    case 3:
      return 'warning'
    case 4:
      return 'warningLight'
    case 5:
      return 'successLight'
  }
}

const renderValidationList = (list, theme) => {
  return (
    <MUIStack
      direction="column"
      alignItems="flex-start"
      justifyContent="flex-start"
      sx={{ width: '100%', mb: 6 }}
      gap={3}
    >
      {list.map((item) => {
        return (
          <MUIStack key={item.label} direction="row" gap="6px" alignItems="center">
            {item.valid ? (
              <CheckIcon
                sx={{ width: '18px', height: '18px', fontSize: '18px', color: theme.palette.success.main, opacity: 1 }}
              />
            ) : (
              <CrossIcon
                sx={{ width: '18px', height: '18px', fontSize: '18px', color: theme.palette.error.main, opacity: 1 }}
              />
            )}

            <MUITypography
              key={item.label}
              variant="body1"
              sx={{ width: '100%', color: item.valid ? 'success.main' : 'error.main', mb: 0 }}
            >
              {item.label}
            </MUITypography>
          </MUIStack>
        )
      })}
    </MUIStack>
  )
}

PasswordInput.propTypes = {
  onChange: PropTypes.func,
  label: PropTypes.string,
  autoFocus: PropTypes.bool,
  autoComplete: PropTypes.string,
  showPasswordStrengthIndicator: PropTypes.bool,
  onValidationChange: PropTypes.func,
  API_DI: PropTypes.func
}
